// app/chimes.js — extracted from App.js (behaviour-preserving split).
import React, { useEffect } from "react";
import { Bell, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getChimeVolume, getChimeVolumePercent, setChimeVolumePercent, subscribeChimeVolume } from "@/lib/chimeVolume";
import {
  getChimePresetId,
  getPresetTones,
  getStaffEventPresetId,
  getStaffEventTones,
  listChimePresets,
  listStaffChimeEvents,
  setChimePresetId,
  setStaffEventPresetId,
  subscribeChimePreset,
  subscribeStaffEventPreset,
} from "@/lib/chimePresets";


// ─────────────────────────────────────────────────────────────────────
// Web-Audio plumbing
// ─────────────────────────────────────────────────────────────────────
// Modern browsers gate audio behind an "autoplay" policy: a new
// AudioContext starts in `suspended` state until a real user gesture
// resumes it. SPAs make this tricky because the gesture (login click)
// happens on a route that immediately unmounts. To make chimes reliable
// across EVERY dashboard, we keep a single module-level AudioContext
// and unlock it inside the login-button click handler. Once unlocked,
// it stays unlocked for the lifetime of the tab — so subsequent chimes
// (login arrival, new kitchen/bar tickets) just schedule into it
// without needing another gesture.
let _sharedAudioCtx = null;
function _getAudioCtx() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!_sharedAudioCtx) {
    try { _sharedAudioCtx = new Ctx(); } catch (_) { return null; }
  }
  return _sharedAudioCtx;
}
// MUST be called inside a real user-gesture event handler (e.g. the
// login button's click). Lazily constructs the shared AudioContext if
// needed and resumes it. No-ops on the server / unsupported browsers.
// Session 27.115 — For iOS Safari and older Chromium mobile builds,
// simply calling ctx.resume() inside a gesture is NOT enough — the
// context stays technically-running but any oscillator started later
// (outside the gesture, e.g. from a WS event) plays silently. The
// fix is to ALSO start a tiny silent oscillator right now, inside
// the gesture. That single-gesture "warm-up" is what actually
// unlocks output; every subsequent chime — whether triggered by a
// tap or by a background poll — is then audible.
export function unlockAudio() {
  try {
    const ctx = _getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().catch(() => { /* noop */ });
    }
    // Silent warm-up burst — 40 ms of near-zero gain. Inaudible in
    // practice but forces iOS/Chromium to allocate the audio worklet
    // and treat future scheduled sources as user-authorised.
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      osc.frequency.value = 440;
      osc.type = "sine";
      osc.connect(gain).connect(ctx.destination);
      const now = ctx.currentTime;
      osc.start(now);
      osc.stop(now + 0.04);
    } catch (_) { /* older browsers — no-op */ }
  } catch (_) { /* best-effort */ }
}

// Legacy generic order chime — kept as a fallback / signature reference.
// Prefer role-specific chimes (playKitchenChime / playBarChime /
// playStaffChime) so audible cues match the source of the notification.
function playOrderChime() {
  try {
    const ctx = _getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().catch(() => {});
    }
    // Session 27.67 — respect the master volume multiplier.
    const master = (() => { try { return getChimeVolume(); } catch (_) { return 1.0; } })();
    if (master <= 0) return; // muted — no scheduling at all
    const now = ctx.currentTime;
    // 5 quick high-pitched dings ~150ms apart. Loud enough to cut
    // through kitchen noise; short enough to not annoy.
    [0, 0.18, 0.36, 0.54, 0.72].forEach((delay) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain).connect(ctx.destination);
      osc.frequency.value = 1180; // bright bell tone
      osc.type = "sine";
      const t = now + delay;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.7 * master, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
      osc.start(t);
      osc.stop(t + 0.18);
    });
  } catch (_) { /* best-effort */ }
}

// ─────────────────────────────────────────────────────────────────────
// Session 27.65 — Loud role-specific chimes with a queue
// ─────────────────────────────────────────────────────────────────────
// User feedback: "one of my clients said they struggle to hear when an
// order comes in — make it very loud, very consistent for a couple of
// seconds instead of just a ping." So each chime now runs for ~2-3
// seconds at a much higher gain envelope than the old 0.5s pings.
//
// Queue behaviour: if a new order arrives WHILE a chime is playing we
// don't overlap sounds (which just muddles into noise). Instead the
// queue holds the next chime and fires it immediately after the
// current one ends, so bursts of orders sound like:
//     [chime A] → [chime B] → [chime C]
// back-to-back without any silence and without any ugly overlap.

const _CHIME_TAIL_MS = 60;        // small gap between queued chimes
export const _VIBRATE_ORDER_MS = 1200;   // per-event vibration length
let _chimeBusyUntil = 0;          // epoch ms when the current chime ends
let _chimeQueue = [];             // pending chime request objects

// Central playback dispatcher — reads the chime queue and schedules
// each chime to start the moment the previous one ends.
function _drainChimeQueue() {
  while (_chimeQueue.length > 0 && Date.now() >= _chimeBusyUntil - 10) {
    const nextReq = _chimeQueue.shift();
    _runChimeNow(nextReq);
  }
}
export function _enqueueChime(req) {
  _chimeQueue.push(req);
  const remaining = _chimeBusyUntil - Date.now();
  if (remaining <= 0) {
    _drainChimeQueue();
  } else {
    // Wake the drainer as soon as the current chime finishes.
    setTimeout(_drainChimeQueue, Math.max(remaining, 10));
  }
}

// Session 27.69 — Master output chain per AudioContext.
//
// Two things were making the kitchen/bar chime feel too soft AND too
// alarm-like:
//   1. Individual oscillators peaked around 0.9 gain and summed
//      naively at the destination — no compression → any layered
//      chord immediately clipped, forcing us to keep gains low.
//   2. Three identical 0.8-second cycles of "bell-bell" fired in a
//      row = the exact rhythmic signature of an ambulance siren.
//
// Fix: route every chime through a DynamicsCompressor + boost
// GainNode so we can push perceived loudness far past what a raw
// oscillator can safely do, without distortion. This is the same
// trick radio broadcasts use to sound "loud" without clipping.
const _masterChain = new WeakMap();
function _getMasterChain(ctx) {
  const cached = _masterChain.get(ctx);
  if (cached) return cached;
  const compressor = ctx.createDynamicsCompressor();
  // Session 27.114 — Gentle compression tuned to eliminate distortion
  // reported on Kitchen/Bar/Staff chimes. Lower threshold + softer
  // knee + slower attack gives the compressor time to actually catch
  // the transient peak of layered oscillators rather than clipping
  // through it, and the *post*-compressor boost has been pulled back
  // from 1.4× to 0.75× so summed peaks stay well under the digital
  // ceiling. Result: same rhythm/character the operators liked,
  // without the gritty clipping edge.
  try { compressor.threshold.setValueAtTime(-24, ctx.currentTime); } catch (_) {}
  try { compressor.knee.setValueAtTime(30, ctx.currentTime); } catch (_) {}
  try { compressor.ratio.setValueAtTime(6, ctx.currentTime); } catch (_) {}
  try { compressor.attack.setValueAtTime(0.006, ctx.currentTime); } catch (_) {}
  try { compressor.release.setValueAtTime(0.3, ctx.currentTime); } catch (_) {}
  const boost = ctx.createGain();
  boost.gain.value = 0.75; // pulled back from 1.4 → 0.75 to kill clipping
  compressor.connect(boost).connect(ctx.destination);
  const chain = { input: compressor };
  _masterChain.set(ctx, chain);
  return chain;
}
function _vibrateBurst(ms = _VIBRATE_ORDER_MS) {
  // Session 27.69 — Prefer the Capacitor Haptics plugin over the raw
  // navigator.vibrate() API. On Capacitor APK WebViews the browser
  // vibrate call is silently blocked by default (no user gesture, or
  // the WebView doesn't map it through to VIBRATE permission), which
  // is why kitchen staff reported feeling nothing even though sound
  // was playing. The Haptics plugin goes through the native VIBRATOR
  // service directly and works reliably. We lazy-import to keep the
  // web bundle small and fall back to navigator.vibrate for browsers.
  try {
    if (typeof window !== "undefined"
        && window.Capacitor
        && typeof window.Capacitor.isNativePlatform === "function"
        && window.Capacitor.isNativePlatform()) {
      // Lazy dynamic import — Capacitor plugins ship as ES modules.
      import("@capacitor/haptics")
        .then(({ Haptics }) => {
          if (Haptics && typeof Haptics.vibrate === "function") {
            Haptics.vibrate({ duration: Math.max(1, Math.round(ms)) })
              .catch(() => { /* best-effort */ });
          }
        })
        .catch(() => { /* plugin unavailable, fall through */ });
      return;
    }
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(ms);
    }
  } catch (_) { /* iOS Safari and older WebViews don't implement vibrate */ }
}

// Schedules one chime — the body of every playXxxChime() function is
// just a description of the tones; this helper actually schedules the
// AudioContext nodes. `req.tones` is an array of
//   { freq, t, dur, gain?, type? }
// where `t` is the offset from the chime's start, `dur` is the tone
// length (seconds), `gain` is the peak gain (0.0-1.0, default 0.85 —
// deliberately loud), and `type` is the oscillator waveform.
function _runChimeNow(req) {
  try {
    const ctx = _getAudioCtx();
    if (!ctx) {
      // Even without audio, still fire the vibrate so silent tablets
      // (kitchen counter with sound off) can catch the event.
      if (req.vibrate) _vibrateBurst(req.vibrateMs || _VIBRATE_ORDER_MS);
      return;
    }
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().catch(() => {});
    }
    // Session 27.67 — apply the per-device master volume multiplier.
    // A value of 0.0 silences ALL chimes on this tablet without
    // disabling vibration (so a manager can mute the sound but still
    // feel the buzz), while 1.0 is the original loud default.
    const master = (() => {
      try { return getChimeVolume(); } catch (_) { return 1.0; }
    })();
    const chain = _getMasterChain(ctx);
    const now = ctx.currentTime;
    let latestEnd = 0;
    (req.tones || []).forEach((tone) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      // Route through compressor + boost instead of directly to destination.
      osc.connect(gain).connect(chain.input);
      osc.type = tone.type || "sine";
      osc.frequency.value = tone.freq;
      const start = now + (tone.t || 0);
      const dur = tone.dur || 0.4;
      const rawPeak = tone.gain != null ? tone.gain : 0.85;
      // Multiplier scales EVERY tone by the same factor so relative
      // balances between layered oscillators are preserved.
      const peak = rawPeak * master;
      gain.gain.setValueAtTime(0, start);
      if (peak > 0) {
        gain.gain.linearRampToValueAtTime(peak, start + 0.015);
        // Hold near-peak briefly so the tone reads as SUSTAINED rather
        // than a decaying ping. This is what makes it "loud and long".
        const holdMs = tone.hold != null ? tone.hold : Math.max(0.02, dur * 0.6);
        gain.gain.linearRampToValueAtTime(peak * 0.9, start + holdMs);
        gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      }
      osc.start(start);
      osc.stop(start + dur + 0.02);
      latestEnd = Math.max(latestEnd, (tone.t || 0) + dur);
    });
    const totalMs = Math.ceil(latestEnd * 1000) + _CHIME_TAIL_MS;
    _chimeBusyUntil = Date.now() + totalMs;
    if (req.vibrate) _vibrateBurst(req.vibrateMs || _VIBRATE_ORDER_MS);
    // Schedule next queue drain right when this chime ends.
    setTimeout(_drainChimeQueue, totalMs);
  } catch (_) { /* best-effort */ }
}

// Session 27.69/27.70 — Kitchen chime. Tone-map is now sourced from
// the currently-selected preset in `chimePresets.js` so managers can
// switch between "Warm Chord", "Bright Bell", "Mellow Marimba" and
// "Digital Chirp" without patching code. The chime queue + master
// compressor chain (see _runChimeNow above) still apply universally,
// so every preset is broadcast-loud without clipping.
export function playKitchenChime() {
  const tones = getPresetTones("kitchen");
  _enqueueChime({ tones, vibrate: true, vibrateMs: _VIBRATE_ORDER_MS });
}

// Session 27.69/27.70 — Bar chime. Same as kitchen but the preset
// library's `bar()` variant is used (one octave higher + brighter
// timbre by default).
export function playBarChime() {
  const tones = getPresetTones("bar");
  _enqueueChime({ tones, vibrate: true, vibrateMs: _VIBRATE_ORDER_MS });
}

// Staff — orders arriving that need confirmation (only when the
// "Confirm orders before sending to kitchen" toggle is ON). Distinct
// from kitchen/bar so a busy staff tablet can tell what happened
// without looking. Softer than kitchen/bar since the person holding
// this device is usually already at the tablet — but still ~2s LOUD.
// Session 27.72 — Now pulls tones from the preset chosen for the
// "new_order" event so managers can distinguish the arrival ping
// from order-ready pings and confirmation pops on the same device.
export function playStaffChime() {
  // Session 27.114 — Unified with Kitchen/Bar so all three dashboards
  // sound identical (operators explicitly asked for this). Uses the
  // same _enqueueChime engine with the selected staff-event preset's
  // kitchen tone-map.
  const tones = getStaffEventTones("new_order");
  _enqueueChime({ tones, vibrate: true, vibrateMs: _VIBRATE_ORDER_MS });
}

// Session 30.19 — Soft long-wait nudge (order pending > 15 min).
export function playLongWaitChime() {
  const tones = getStaffEventTones("long_wait");
  _enqueueChime({ tones, vibrate: true, vibrateMs: 120 });
}

export function playOrderReadyChime() {
  // Session 27.114 — Unified engine so Staff order-ready chime matches
  // Kitchen/Bar sound quality (no HTML5-Audio distortion difference).
  const tones = getStaffEventTones("order_ready");
  _enqueueChime({ tones, vibrate: true, vibrateMs: _VIBRATE_ORDER_MS });
}

export function playStaffConfirmationChime() {
  const tones = getStaffEventTones("confirmation");
  _enqueueChime({ tones, vibrate: false });
}

// Bridge to notify.js — expose playOrderReadyChime on window so the
// notify module can trigger the SAME queued chime without importing
// App.js (which would create a circular dependency). Session 27.65.
if (typeof window !== "undefined") {
  try {
    window.__nfcsPlayOrderReadyChime = playOrderReadyChime;
    // Session 31.8 — Retail dashboard plays the same "order needs approval" chime.
    window.__nfcsPlayStaffConfirmationChime = playStaffConfirmationChime;
  } catch (_) { /* noop */ }
}

// ─────────────────────────────────────────────────────────────────────
// Session 27.67 — Chime volume control component
// ─────────────────────────────────────────────────────────────────────
// A compact popover trigger + slider that adjusts the master chime
// volume for this device. Used on the Kitchen, Bar, and Staff
// dashboards so a manager can quickly turn the loud order chime up
// or down without opening a settings screen. Value is stored in
// localStorage and applied globally to every chime (see _runChimeNow).
//
// Props:
//   role — "kitchen" | "bar" | "staff" (used only for data-testid
//          namespacing and the test-chime button behaviour).
//   variant — "outline" | "ghost" — controls the trigger button styling.
//
// The popover shows:
//   * A "Chime volume" label with the current percent (e.g. "75%")
//   * A slider from 0 to 100
//   * A "Play test" button that fires the role-appropriate chime at
//     the CURRENT slider value so managers hear the volume they're
//     about to set before committing.
//   * A "Mute" toggle that snaps to 0 without losing the previous
//     value (tapping unmute restores the prior setting).
export function ChimeVolumeControl({ role, variant = "outline" }) {
  const [percent, setPercent] = React.useState(() => {
    try { return getChimeVolumePercent(); } catch (_) { return 100; }
  });
  const [lastNonZero, setLastNonZero] = React.useState(() => {
    try {
      const p = getChimeVolumePercent();
      return p > 0 ? p : 80;
    } catch (_) { return 80; }
  });
  // Session 27.70 — preset picker state, in sync with the shared
  // chimePresets store so multiple open popovers stay coherent.
  const [presetId, setPresetIdState] = React.useState(() => {
    try { return getChimePresetId(); } catch (_) { return "warm_chord"; }
  });
  const presets = React.useMemo(() => {
    try { return listChimePresets(); } catch (_) { return []; }
  }, []);
  // Session 27.72 — Per-event preset state for the Staff dashboard.
  // Each of the three staff events (new order, order ready ping,
  // order confirmation pop-up) has an independent preset selection.
  const staffEvents = React.useMemo(() => {
    if (role !== "staff") return [];
    try { return listStaffChimeEvents(); } catch (_) { return []; }
  }, [role]);
  const [staffEventPresets, setStaffEventPresets] = React.useState(() => {
    if (role !== "staff") return {};
    try {
      return {
        new_order:    getStaffEventPresetId("new_order"),
        order_ready:  getStaffEventPresetId("order_ready"),
        confirmation: getStaffEventPresetId("confirmation"),
        long_wait:    getStaffEventPresetId("long_wait"), // Session 30.19
      };
    } catch (_) { return {}; }
  });
  useEffect(() => {
    // Keep the local state in sync when the value is changed from
    // another slider on a different dashboard (rare, but possible if
    // someone opens two tabs).
    const unsubVol = subscribeChimeVolume((v) => {
      const pct = Math.round(v * 100);
      setPercent(pct);
      if (pct > 0) setLastNonZero(pct);
    });
    const unsubPreset = subscribeChimePreset((id) => setPresetIdState(id));
    // Session 27.72 — Also subscribe to per-event preset changes for
    // staff role so opening the popover on a second staff tablet
    // reflects the latest choice made elsewhere.
    const staffUnsubs = [];
    if (role === "staff") {
      ["new_order", "order_ready", "confirmation", "long_wait"].forEach((event) => {
        try {
          const u = subscribeStaffEventPreset(event, (id) => {
            setStaffEventPresets((prev) => ({ ...prev, [event]: id }));
          });
          staffUnsubs.push(u);
        } catch (_) { /* noop */ }
      });
    }
    return () => {
      try { unsubVol(); } catch (_) { /* noop */ }
      try { unsubPreset(); } catch (_) { /* noop */ }
      staffUnsubs.forEach((u) => { try { u(); } catch (_) { /* noop */ } });
    };
  }, [role]);

  const testChime = React.useCallback(() => {
    try { unlockAudio(); } catch (_) { /* noop */ }
    // Small delay so unlockAudio's ctx.resume() has time to complete.
    setTimeout(() => {
      try {
        if (role === "kitchen") playKitchenChime();
        else if (role === "bar") playBarChime();
        else if (role === "runner") playKitchenChime(); // Runner shares Kitchen/Bar preset engine
        else playStaffChime();
      } catch (_) { /* noop */ }
    }, 40);
  }, [role]);

  // Session 27.72 — Per-event "Play test" helper for the staff
  // dashboard so managers can preview the exact chime that will
  // fire for each of the three events before committing.
  const testStaffEventChime = React.useCallback((event) => {
    try { unlockAudio(); } catch (_) { /* noop */ }
    setTimeout(() => {
      try {
        const tones = getStaffEventTones(event);
        _enqueueChime({ tones, vibrate: event !== "confirmation", vibrateMs: _VIBRATE_ORDER_MS });
      } catch (_) { /* noop */ }
    }, 40);
  }, []);

  const muted = percent === 0;
  const toggleMute = () => {
    if (muted) {
      setChimeVolumePercent(lastNonZero || 80);
    } else {
      setLastNonZero(percent);
      setChimeVolumePercent(0);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          className="rounded-xl"
          title={muted ? "Chimes are muted for this device — tap to adjust volume" : `Chime volume: ${percent}% (tap to adjust)`}
          data-testid={`${role}-chime-volume-trigger`}
          aria-label={`Chime volume ${muted ? "muted" : `${percent}%`}`}
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          <span className="hidden sm:inline">
            {muted ? "Muted" : `${percent}%`}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className={role === "staff" ? "w-80" : "w-72"}
        data-testid={`${role}-chime-volume-popover`}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">Chime volume</p>
              <p className="text-[11px] text-muted-foreground">
                Applies to every sound on this device — new orders and order-ready pings.
              </p>
            </div>
            <span
              className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] font-mono tabular-nums"
              data-testid={`${role}-chime-volume-percent`}
            >
              {muted ? "MUTE" : `${percent}%`}
            </span>
          </div>
          {/* Session 27.70 — Preset picker. Only shown when there are
              actual choices (always ≥ 2 in normal builds). Changing
              the preset takes effect on the NEXT chime — the queue
              doesn't interrupt anything already playing.
              Session 27.72 — On Staff dashboards this device-wide
              picker is replaced by three per-event pickers below,
              so we skip rendering it here to avoid confusion. */}
          {role !== "staff" && presets.length > 1 ? (
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium">Chime style</Label>
              <Select
                value={presetId}
                onValueChange={(id) => { setChimePresetId(id); setPresetIdState(id); }}
              >
                <SelectTrigger
                  className="h-9 rounded-lg text-xs"
                  data-testid={`${role}-chime-preset-trigger`}
                >
                  <SelectValue placeholder="Pick a chime style" />
                </SelectTrigger>
                <SelectContent>
                  {presets.map((p) => (
                    <SelectItem
                      key={p.id}
                      value={p.id}
                      data-testid={`${role}-chime-preset-item-${p.id}`}
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold">{p.label}</span>
                        <span className="text-[10px] text-muted-foreground">{p.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {/* Session 27.72 — Per-event chime picker (Staff only).
              Three dropdowns let managers pick a distinct preset for
              new-order, order-ready and order-confirmation pop-up
              events. Each event has a compact "▶︎ Test" button beside
              it so the choice can be previewed before committing. */}
          {role === "staff" && presets.length > 1 && staffEvents.length > 0 ? (
            <div className="space-y-2.5 rounded-lg border border-border/60 bg-secondary/30 p-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Choose a sound for each event
              </p>
              {staffEvents.map(({ event, label }) => (
                <div
                  key={event}
                  className="space-y-1"
                  data-testid={`staff-event-chime-row-${event}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-[11px] font-medium">{label}</Label>
                    <button
                      type="button"
                      className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--accent))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]/40 focus-visible:rounded"
                      onClick={() => testStaffEventChime(event)}
                      disabled={muted}
                      data-testid={`staff-event-chime-test-${event}`}
                    >
                      ▶ Test
                    </button>
                  </div>
                  <Select
                    value={staffEventPresets[event] || ""}
                    onValueChange={(id) => {
                      try { setStaffEventPresetId(event, id); } catch (_) { /* noop */ }
                      setStaffEventPresets((prev) => ({ ...prev, [event]: id }));
                    }}
                  >
                    <SelectTrigger
                      className="h-8 rounded-lg text-xs"
                      data-testid={`staff-event-chime-trigger-${event}`}
                    >
                      <SelectValue placeholder="Pick a chime style" />
                    </SelectTrigger>
                    <SelectContent>
                      {presets.map((p) => (
                        <SelectItem
                          key={p.id}
                          value={p.id}
                          data-testid={`staff-event-chime-item-${event}-${p.id}`}
                        >
                          <span className="text-xs font-semibold">{p.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          ) : null}
          <Slider
            min={0}
            max={100}
            step={5}
            value={[percent]}
            onValueChange={(v) => {
              const p = Array.isArray(v) ? v[0] : v;
              setChimeVolumePercent(p);
              if (p > 0) setLastNonZero(p);
            }}
            className="w-full"
            data-testid={`${role}-chime-volume-slider`}
            aria-label="Chime volume"
          />
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-lg text-xs"
              onClick={toggleMute}
              data-testid={`${role}-chime-volume-mute`}
            >
              {muted ? <Volume2 className="mr-1.5 h-3.5 w-3.5" /> : <VolumeX className="mr-1.5 h-3.5 w-3.5" />}
              {muted ? "Unmute" : "Mute"}
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-lg text-xs"
              onClick={testChime}
              disabled={muted}
              data-testid={`${role}-chime-volume-test`}
            >
              <Bell className="mr-1.5 h-3.5 w-3.5" />
              Play test
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Saved on this tablet. Other devices keep their own volume.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Futuristic login chime — a short 3-note arpeggio that plays on
// successful sign-in across every dashboard (Staff/Owner/Kitchen/Bar/Admin).
function playLoginChime() {
  try {
    const ctx = _getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().catch(() => {});
    }
    // Session 27.67 — respect the master volume multiplier.
    const master = (() => { try { return getChimeVolume(); } catch (_) { return 1.0; } })();
    if (master <= 0) return;
    const now = ctx.currentTime;
    // Bright ascending arpeggio: E5 → B5 → E6. Slight detune layer for
    // a "sparkle" / futuristic shimmer feel.
    const notes = [
      { freq: 659.25, t: 0.00, dur: 0.22 }, // E5
      { freq: 987.77, t: 0.12, dur: 0.22 }, // B5
      { freq: 1318.5, t: 0.24, dur: 0.32 }, // E6
    ];
    notes.forEach(({ freq, t, dur }) => {
      [0, 4].forEach((detune) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain).connect(ctx.destination);
        osc.type = "triangle";
        osc.frequency.value = freq;
        osc.detune.value = detune;
        const start = now + t;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime((detune ? 0.18 : 0.32) * master, start + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
        osc.start(start);
        osc.stop(start + dur + 0.02);
      });
    });
  } catch (_) { /* best-effort */ }
}

// Login-chime trigger lives on the DASHBOARD side (not on the login form)
// so the chime fires reliably after navigation, regardless of which
// dashboard is being entered. The login form sets a one-shot flag in
// sessionStorage right before navigating; the destination dashboard
// reads + clears the flag on mount and plays the chime. Page refreshes
// or auto-redirects from a stored session don't re-trigger the chime.
const LOGIN_CHIME_FLAG = "login_chime_pending";
export function armLoginChime() {
  try {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(LOGIN_CHIME_FLAG, "1");
    }
  } catch (_) { /* best-effort */ }
}
export function useLoginChimeOnMount() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Defensive: attempt to unlock the shared AudioContext immediately
    // (works if a gesture already happened, e.g. the login click), and
    // also attach one-shot listeners so the very first tap/keypress on
    // this dashboard unlocks audio — covers users arriving via saved
    // session without a fresh login click.
    unlockAudio();
    const onGesture = () => { unlockAudio(); };
    window.addEventListener("pointerdown", onGesture, { once: true, capture: true });
    window.addEventListener("keydown", onGesture, { once: true, capture: true });

    let timer = null;
    try {
      if (window.sessionStorage.getItem(LOGIN_CHIME_FLAG) === "1") {
        window.sessionStorage.removeItem(LOGIN_CHIME_FLAG);
        // Tiny delay lets the dashboard's first render settle before audio
        // starts — avoids any unmount race from the prior login screen.
        timer = setTimeout(() => playLoginChime(), 60);
      }
    } catch (_) { /* noop */ }

    return () => {
      if (timer) clearTimeout(timer);
      try {
        window.removeEventListener("pointerdown", onGesture, { capture: true });
        window.removeEventListener("keydown", onGesture, { capture: true });
      } catch (_) { /* noop */ }
    };
  }, []);
}
